import React, { useState, useEffect } from "react";

// Material UI
import Fab from '@mui/material/Fab';
import { Paper, Typography, Grid } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import VerticalAlignTopOutlinedIcon from '@mui/icons-material/VerticalAlignTopOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// Components
import { DataGrid, ViewButton, FabContainer } from '@arredo/reactjs-ui';
import MovementFilters from "./filters";
import CreateEgressModal from './modals/createEgress';
import DetailEgressModal from './modals/detailEgress';

// Hooks
import useCashRegisterImprestMovements from '../../../hooks/cashRegisterMovements/useCashRegisterImprestMovements';
import useCashRegisters from "../../../hooks/cashRegisters/useCashRegisters";
import useAuthorize from '../../../hooks/user/useAuthorize';
import useMonthlyImprest from '../../../hooks/cashRegisters/useMonthlyImprest';

// Models
import { titleImprest, columns } from '../../../models/CashRegisterMovementEntity';
import { Permissions } from '../../../models/Roles';

// Others
import useStyles from './styles';
import { utils } from "../../../utils/utils";

const CashRegisterImprestMovements = () => {
    const isAuthorized = useAuthorize();
    const { cashRegisters } = useCashRegisters({ isImprest: true });
    const { monthlyImprest, loading: loadingImprest } = useMonthlyImprest();
    const { classes } = useStyles();
    const [cashRegister, setCashRegister] = useState()
    const [createEgressModalOpen, setCreateEgressModalOpen] = useState(false);
    const [detailEgressModalOpen, setDetailEgressModalOpen] = useState(false);
    const [cashRegisterMovement, setCashRegisterMovement] = useState({});
    const { cashRegisterMovements, loading, totalPage, filters, setParameterSearch, clearFilters, setCashRegisterId } = useCashRegisterImprestMovements();

    const handleChangePage = (e, page) => {
        setParameterSearch({
            ...filters,
            pageNumber: Number(page)
        });
    }

    useEffect(() => {
        if (cashRegisters) {
            const CRSelected = cashRegisters?.find(cr => cr.isImprest);
            setCashRegister(CRSelected);
            setCashRegisterId(CRSelected?.id);
        }
    }, [cashRegisters, setCashRegisterId]);

    const handleFilterChange = (event) =>
        setParameterSearch({
            ...filters,
            [event.target.name]: event.target.value,
            pageNumber: 1
        });

    const handleSaveCreateEgress = () => {
        setCreateEgressModalOpen(false);
        window.location.reload()
    }

    const handleViewCashRegisterMovement = (item) => {
        setCashRegisterMovement(item);
        setDetailEgressModalOpen(true);
    }
    return (
        <>
            <Grid size="grow">
                <Paper className={classes.paper}>
                    <div className={classes.column}>
                        <Typography variant='h4'>
                            {titleImprest}
                        </Typography>
                        <Typography variant='h6'>
                            {`${cashRegister && cashRegister.name ? `Caja: ${cashRegister.name}` : ''}`}
                        </Typography>
                    </div>
                    <div className={classes.rigthHeader} id="cashOpening">
                        <Grid container alignItems='center' justifyContent='space-between' size={12}>
                            <Grid size={7}>
                                <Typography color='primary' variant='subtitle1' component='p'>
                                    <strong>Disponible autorizado:</strong>
                                </Typography>
                            </Grid>
                            <Grid container justifyContent='flex-end' size={5}>
                                {
                                    loadingImprest
                                        ? <CircularProgress size="1.5rem" />
                                        : <Typography variant='body1'>
                                            $ {utils.formatMoney(monthlyImprest?.fixedAmount)}
                                        </Typography>
                                }
                            </Grid>
                        </Grid>
                        <Grid container alignItems='center' justifyContent='space-between' size={12}>
                            <Grid size={7}>
                                <Typography color='primary' variant='subtitle1' component='p'>
                                    <strong>Real efectivo:</strong>
                                </Typography>
                            </Grid>
                            <Grid container justifyContent='flex-end' size={5}>
                                {
                                    loadingImprest
                                        ? <CircularProgress size="1.5rem" />
                                        : <Typography variant='body1'>
                                            $ {utils.formatMoney(monthlyImprest?.totalAmountImprest)}
                                        </Typography>
                                }
                            </Grid>
                        </Grid>
                    </div>
                </Paper>
            </Grid>
            {
                createEgressModalOpen &&
                <CreateEgressModal
                    open={createEgressModalOpen}
                    onClose={() => setCreateEgressModalOpen(false)}
                    onSave={handleSaveCreateEgress}
                    cashRegister={cashRegister}
                    monthlyImprest={monthlyImprest}
                />
            }
            {
                detailEgressModalOpen &&
                <DetailEgressModal
                    open={detailEgressModalOpen}
                    onClose={() => setDetailEgressModalOpen(false)}
                    cashRegisterMovement={cashRegisterMovement}
                />
            }
            <MovementFilters
                filters={filters}
                clearFilters={clearFilters}
                onFilterChange={handleFilterChange}
            />
            <DataGrid
                columns={columns}
                rows={cashRegisterMovements}
                loading={loading}
                paginationProps={{
                    count: totalPage,
                    page: filters?.pageNumber || 1,
                    onChange: handleChangePage
                }}
                actions={({ row }) => (
                    <>
                        {isAuthorized([Permissions.CashregisterView]) && (
                            <ViewButton
                                onClick={() => handleViewCashRegisterMovement(row)}
                            />

                        )}
                        {
                            row?.fileUrl &&
                            <a href={row.fileUrl} target='_blank' rel="noreferrer">
                                <IconButton color="primary" size="large">
                                    <AttachFileIcon />
                                </IconButton>
                            </a>
                        }
                    </>
                )}

            />
            {isAuthorized([Permissions.CashregisterCreate]) &&
                <FabContainer>
                    <Fab
                        color='primary'
                        variant="extended"
                        className={classes.fab}
                        disabled={monthlyImprest === undefined}
                        onClick={() => setCreateEgressModalOpen(true)}
                    >
                        <VerticalAlignTopOutlinedIcon className={classes.extendedIcon} />
                        Egreso
                    </Fab>
                </FabContainer>}
        </>
    );
}

export default CashRegisterImprestMovements;